/**
 * The two-tab "add attachment" modal — §2.8 #90.
 *
 * Stays OPEN after a successful add and shows an inline confirmation instead of
 * closing, so a user attaching three lessons does not reopen it three times.
 * That is why `lastAdded` lives here rather than in either child: it has to
 * survive a switch between the Lesson and Link tabs.
 *
 * One body, two frames: a dialog from `sm`, and below it a bottom sheet that
 * fits its content. The fixed 620px dialog was two-thirds empty on a phone.
 */
import { useEffect, useState } from 'react';
import { Modal, Sheet, useSlidingPill } from '@ai-tutor/ui';
import { BookOpen, Check, Link as LinkIcon } from 'lucide-react';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { useTransientValue } from '@/hooks/ui/useTransientValue';
import type { ExtrasAttachment } from '@/lib/api-client';
import { LessonPicker } from './LessonPicker';
import { LinkForm } from './LinkForm';

const TABS = [
  { id: 'lesson', label: 'Lesson', icon: BookOpen },
  { id: 'link', label: 'Link', icon: LinkIcon },
] as const;

export interface AddAttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonId: string;
  existingAttachments: ExtrasAttachment[];
}

export function AddAttachmentModal({
  isOpen,
  onClose,
  lessonId,
  existingAttachments,
}: AddAttachmentModalProps) {
  const [tab, setTab] = useState<'lesson' | 'link'>('lesson');
  // `lastAdded` powers the inline "Added X" toast that lives at the
  // bottom of the modal body. When set, the toast fades in for 2s then
  // clears itself. Tracked at the modal level so it persists across
  // tab switches.
  // The state + timer ref + clearing setter + unmount cleanup that used to sit
  // here are `useTransientValue` — they only ever worked as a set, and the same
  // four-part pattern is hand-rolled in ~5 other places in the app.
  const {
    value: lastAdded,
    show: showLastAdded,
    reset: resetLastAdded,
  } = useTransientValue<string>(2400);

  // Reset on each open so the user always lands on Lesson with a clean slate.
  useEffect(() => {
    if (isOpen) {
      setTab('lesson');
      resetLastAdded();
    }
  }, [isOpen, resetLastAdded]);

  const handleAdded = (title: string) => {
    showLastAdded(title);
  };

  const isWide = useMediaQuery('(min-width: 640px)');
  // Stable min-height (dialog only) keeps the frame from jumping when the
  // user toggles between Lesson (long list) and Link (short form). The
  // picker still scrolls internally above this floor.
  const content = (
    <AttachmentBody
      tab={tab}
      onTab={setTab}
      lessonId={lessonId}
      existingAttachments={existingAttachments}
      onAdded={handleAdded}
      lastAdded={lastAdded}
    />
  );

  if (!isWide) {
    return (
      <Sheet
        isOpen={isOpen}
        onClose={onClose}
        side='bottom'
        size='auto'
        title='Add attachment'
      >
        <div className='min-h-0 flex-1 overflow-y-auto'>{content}</div>
      </Sheet>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Add attachment'
      size='lg'
      // Scale-in entrance - matches the rhythm of `animate-fade-in`
      // surfaces elsewhere (OnDemandWrapper) so the modal feels native.
      contentClassName='animate-scale-in'
    >
      {content}
    </Modal>
  );
}

interface AttachmentBodyProps {
  tab: 'lesson' | 'link';
  onTab: (tab: 'lesson' | 'link') => void;
  lessonId: string;
  existingAttachments: ExtrasAttachment[];
  onAdded: (title: string) => void;
  lastAdded: string | null | undefined;
}

/** Tabs, the active tab's form, and the inline "Added X" - in either frame. */
function AttachmentBody({
  tab,
  onTab,
  lessonId,
  existingAttachments,
  onAdded,
  lastAdded,
}: AttachmentBodyProps) {
  const { containerRef: tabBarRef, pill } = useSlidingPill(
    tab,
    'rounded-full bg-primary shadow-sm'
  );
  return (
    <div className='flex flex-col px-4 py-4 sm:min-h-[620px] sm:px-6 sm:py-5'>
      {/* The app's pill tabs (as in Text / Podcast, Flashcards / Quiz),
          with the same sliding active pill, on their own row above the
          description rather than squeezed beside it. */}
      <div ref={tabBarRef} className='relative mb-3 flex gap-2 self-start'>
        {pill}
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type='button'
            onClick={() => onTab(id)}
            aria-pressed={tab === id}
            data-pill-active={tab === id ? 'true' : undefined}
            className={`tap-target relative z-10 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
              tab === id
                ? 'text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            <Icon className='h-4 w-4' />
            {label}
          </button>
        ))}
      </div>
      <p className='mb-4 text-sm text-muted-foreground'>
        Link a lesson from your library or save an external URL for later.
      </p>

      <div className='flex-1'>
        {tab === 'lesson' ? (
          <LessonPicker
            lessonId={lessonId}
            existingAttachments={existingAttachments}
            onAdded={onAdded}
          />
        ) : (
          <LinkForm lessonId={lessonId} onAdded={onAdded} />
        )}
      </div>

      {/* Inline success - appears at the bottom of the body so the
          user sees it without losing context. Fades out after 2.4s. */}
      {lastAdded && (
        <div
          key={lastAdded}
          className='mt-4 flex animate-fade-in items-center gap-2 rounded-md border border-practice/50 bg-practice/10 px-3 py-2 text-sm text-foreground [&_svg]:text-practice'
        >
          <Check className='h-4 w-4 flex-shrink-0' />
          <span className='truncate'>
            Added <span className='font-medium'>{lastAdded}</span>
          </span>
        </div>
      )}
    </div>
  );
}
